import {CustomMarquee, HorizontalScrollDirection} from "~/components/customMarquee";

/* ============================================================================
 * BuiltWithFounders, direct port of growthjockey--website/app/components/
 * sections/growthpreneurs.tsx.
 *
 * Dual-tone GJ heading + an infinite-scrolling marquee of partner brand
 * logos. Each logo has a monochrome (dark) variant at rest and a full-color
 * variant on hover; the two images are stacked absolutely and cross-faded
 * with opacity. Hover anywhere over the marquee pauses scroll (CSS in
 * .marquee:hover). 17 brands sourced from GJ's CDN.
 * ============================================================================ */

/* Filenames are counterintuitive: `white-X.webp` files contain the DARK logo
 * variants (intended for placement on white backgrounds); `Dark-X.webp` files
 * contain the WHITE/inverted variants (intended for dark backgrounds). Two
 * brands (Mfree, DailyBloom) use bespoke filenames instead of the `white-N`
 * pattern. We use the light-theme variants here since the page sits on
 * #f7f7f7. Hover swaps to the full-color "Original" variant. */
const trustedBrands: Array<{src: string; hoversrc: string; altText: string}> = [
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-1.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/Dr.ReddyOriginal.webp",
        altText: "Dr. Reddy's",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-3.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/LivpureOriginal.webp",
        altText: "Livpure",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-4.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/EmotoradOriginal.webp",
        altText: "EMotorad",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/mfreeNew.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/MfreeOriginal.webp",
        altText: "Mfree",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-6.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/AmpereOriginal.webp",
        altText: "Ampere",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-8.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/PolyCabOriginal.webp",
        altText: "Polycab",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-9.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/TrumigoOriginal.webp",
        altText: "Trumigo",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-10.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/SammanCapitalOriginal.webp",
        altText: "Sammaan Capital",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-11.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/WorldSimOriginal.webp",
        altText: "WorldSim",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/dailybloomNew.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/DailybloomOriginal.webp",
        altText: "DailyBloom",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-13.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/LectrixOriginal.webp",
        altText: "Lectrix",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-17.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/ApolloOriginal.webp",
        altText: "Apollo",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-18.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/LivguardOriginal.webp",
        altText: "Livguard",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-19.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/SleepyHugOriginal.webp",
        altText: "SleepyHug",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-21.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/GreavesOriginal.webp",
        altText: "Greaves",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white-22.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/YoloBusOriginal.webp",
        altText: "YoloBus",
    },
    {
        src: "https://growthjockey-prod.b-cdn.net/new/white.webp",
        hoversrc: "https://growthjockey-prod.b-cdn.net/new/OriginalLogos/DuluxOriginal.webp",
        altText: "Dulux",
    },
];

export function BuiltWithFounders() {
    return (
        <div className="grid grid-cols-1 grid-flow-row gj-pb-section-divider max-lg:pt-3">
            <div className="lg:grid h-fit overflow-hidden lg:py-lg">
                <CustomMarquee
                    scrollDirection={HorizontalScrollDirection.leftToRight}
                    animationDuration={50}
                    marqueeElements={trustedBrands.map((item) => (
                        <li
                            key={item.altText}
                            className="h-[3.125rem] lg:h-[5.25rem] relative group overflow-hidden"
                        >
                            <img
                                loading="lazy"
                                src={item.src}
                                alt={item.altText}
                                className="h-full w-auto object-contain transition-opacity duration-300 opacity-100 group-hover:opacity-0"
                            />
                            <img
                                loading="lazy"
                                src={item.hoversrc}
                                alt=""
                                aria-hidden="true"
                                className="h-full w-auto object-contain absolute inset-0 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                            />
                        </li>
                    ))}
                />
            </div>
        </div>
    );
}
